import { useEffect, useState, type FormEvent } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  Package,
  Phone,
  User,
} from "lucide-react";

import { formatPhone, isValidPhone } from "@montebox/shared/people";

import { PasswordChecklist, isStrongPassword } from "@/components/PasswordChecklist";
import { useSession } from "@/lib/session";
import { cn } from "@/lib/utils";
import { SiteCredit } from "@/components/SiteCredit";

/**
 * Entrar / criar conta do cliente.
 *
 * Montar nao pede conta; salvar e enviar o pedido pedem — e na conta que o
 * pedido mora e que o cliente acompanha as etapas. Ao criar a conta, os
 * pedidos ja feitos com o mesmo WhatsApp passam a pertencer a ela.
 */
export const Route = createFileRoute("/minha-conta/entrar")({
  head: () => ({ meta: [{ title: "Minha conta — MonteBox" }] }),
  component: ClienteLogin,
});

type Tab = "entrar" | "criar";

function ClienteLogin() {
  const navigate = useNavigate();
  const { login, register, profile } = useSession();

  const [tab, setTab] = useState<Tab>("entrar");
  const [name, setName] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const entering = tab === "entrar";
  // Criar conta só libera com senha forte e WhatsApp completo — a lista
  // abaixo da senha mostra o que falta, regra por regra.
  const phoneIncomplete = whatsapp !== "" && !isValidPhone(whatsapp);
  const ready = isStrongPassword(password, { name, email }) && isValidPhone(whatsapp);

  useEffect(() => {
    if (profile?.role === "CLIENTE") {
      void navigate({ to: "/minha-conta/pedidos", replace: true });
    }
  }, [profile, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const next = entering
        ? await login(email, password)
        : await register({ name, email, password, whatsapp });

      if (next.role === "ADMIN" || next.role === "ATENDENTE") {
        // Conta da equipe entrando pela porta do cliente.
        await navigate({ to: "/admin/pedidos", replace: true });
        return;
      }
      await navigate({ to: "/minha-conta/pedidos", replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Nao foi possivel continuar");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-white">
      {/* Painel de marca */}
      <div className="hidden w-[620px] shrink-0 flex-col justify-between bg-mb-navy p-12 lg:flex">
        <Link
          to="/"
          className="flex items-center gap-2.5"
          aria-label="MonteBox — voltar para a loja"
        >
          <span className="grid h-9 w-9 place-items-center rounded-full bg-mb-yellow shadow-cta">
            <Package className="h-5 w-5 text-mb-ink" />
          </span>
          <span className="font-display text-lg font-bold tracking-tight text-white">MonteBox</span>
        </Link>

        <div>
          <div className="preview-stage relative h-80 w-80 overflow-hidden rounded-mb-card">
            <img
              src="/assets/camiseta-base-frente.png"
              alt=""
              className="absolute inset-0 h-full w-full object-contain"
            />
          </div>
          <h1 className="mt-8 max-w-[460px] font-display text-4xl/10 font-bold tracking-tight text-white text-pretty">
            Seus uniformes ficam guardados aqui.
          </h1>
          <p className="mt-3 max-w-[420px] text-base text-white/72 text-pretty">
            Entre para reabrir um pedido antigo, repetir a mesma configuração e acompanhar o
            orçamento.
          </p>
        </div>

        <p className="text-xs text-white/50">
          Monte à vontade sem conta. Para salvar e enviar o pedido, entre ou crie a sua.
        </p>
      </div>

      {/* Formulário */}
      <div className="relative flex grow items-center justify-center px-6 pb-16 pt-20 sm:p-12 sm:pb-16 sm:pt-24">
        {/* Volta para a loja — no celular o painel da marca some, e esta é a saída. */}
        <Link
          to="/"
          className="absolute left-6 top-6 inline-flex items-center gap-1.5 text-sm font-semibold text-mb-muted transition-colors hover:text-mb-navy sm:left-12 sm:top-10"
        >
          <ArrowLeft className="h-4 w-4" />
          Voltar para a loja
        </Link>
        <form className="w-full max-w-[420px]" onSubmit={(event) => void handleSubmit(event)}>
          <div className="flex gap-1 rounded-full bg-mb-surface p-1">
            {(["entrar", "criar"] as Tab[]).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setTab(option);
                  setError(null);
                }}
                aria-pressed={tab === option}
                className={cn(
                  "grow rounded-full px-4 py-2 text-sm font-semibold transition-colors",
                  tab === option
                    ? "bg-white text-mb-navy shadow-card"
                    : "text-mb-muted hover:text-mb-navy",
                )}
              >
                {option === "entrar" ? "Entrar" : "Criar conta"}
              </button>
            ))}
          </div>

          <h2 className="mt-8 font-display text-3xl font-bold tracking-tight text-mb-navy">
            {entering ? "Entrar na minha conta" : "Criar minha conta"}
          </h2>
          <p className="mt-2 text-sm text-mb-muted">
            {entering
              ? "Use o e-mail que você informou no seu último pedido."
              : "Use o mesmo WhatsApp dos seus pedidos: eles entram na conta automaticamente."}
          </p>

          <div className="mt-8 flex flex-col gap-5">
            {!entering && (
              <>
                <Field label="Nome" icon={<User className="h-[18px] w-[18px] text-mb-muted" />}>
                  <input
                    type="text"
                    autoComplete="name"
                    required
                    minLength={2}
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder="Como podemos te chamar"
                    className="w-full bg-transparent text-sm text-mb-ink outline-none placeholder:text-mb-muted"
                  />
                </Field>
                <Field
                  label="WhatsApp"
                  icon={<Phone className="h-[18px] w-[18px] text-mb-muted" />}
                  hint={
                    phoneIncomplete
                      ? "Com DDD: (XX) XXXX-XXXX ou (XX) X XXXX-XXXX."
                      : "É por aqui que a equipe fala com você sobre o pedido."
                  }
                  invalid={phoneIncomplete}
                >
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    required
                    value={whatsapp}
                    onChange={(event) => setWhatsapp(formatPhone(event.target.value))}
                    placeholder="(11) 9 9999-0000"
                    className="w-full bg-transparent text-sm text-mb-ink outline-none placeholder:text-mb-muted"
                  />
                </Field>
              </>
            )}

            <Field label="E-mail" icon={<Mail className="h-[18px] w-[18px] text-mb-muted" />}>
              <input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="voce@exemplo.com.br"
                className="w-full bg-transparent text-sm text-mb-ink outline-none placeholder:text-mb-muted"
              />
            </Field>

            <Field label="Senha" icon={<Lock className="h-[18px] w-[18px] text-mb-muted" />}>
              <input
                type={showPassword ? "text" : "password"}
                autoComplete={entering ? "current-password" : "new-password"}
                required
                minLength={entering ? 1 : 8}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                className="w-full grow bg-transparent text-base text-mb-ink outline-none placeholder:tracking-[0.2em] placeholder:text-mb-ink"
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                className="shrink-0 text-mb-muted transition-colors hover:text-mb-navy"
              >
                {showPassword ? (
                  <EyeOff className="h-[18px] w-[18px]" />
                ) : (
                  <Eye className="h-[18px] w-[18px]" />
                )}
              </button>
            </Field>
            {!entering && (
              <PasswordChecklist
                password={password}
                name={name}
                email={email}
                className="-mt-2 text-mb-muted"
              />
            )}
            {entering && (
              <Link
                to="/esqueci-senha"
                className="-mt-2 self-end text-sm font-semibold text-mb-navy hover:text-mb-ink"
              >
                Esqueci minha senha
              </Link>
            )}

            {error && (
              <p
                role="alert"
                className="flex items-start gap-2 rounded-mb-field border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || (!entering && !ready)}
              className="flex w-full items-center justify-center gap-2 rounded-mb-button bg-mb-yellow px-6 py-4 font-display text-base font-bold text-mb-ink shadow-cta transition-transform hover:-translate-y-0.5 disabled:opacity-60"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {entering ? "Entrando…" : "Criando…"}
                </>
              ) : (
                <>
                  {entering ? "Entrar" : "Criar conta"}
                  <ArrowRight className="h-5 w-5" />
                </>
              )}
            </button>

            <p className="text-center text-sm text-mb-muted">
              {entering ? "Ainda não tem conta? " : "Já tem conta? "}
              <button
                type="button"
                onClick={() => setTab(entering ? "criar" : "entrar")}
                className="font-semibold text-mb-navy hover:text-mb-ink"
              >
                {entering ? "Criar agora" : "Entrar"}
              </button>
            </p>
          </div>
        </form>
        <SiteCredit className="absolute inset-x-6 bottom-4" />
      </div>
    </div>
  );
}

/** Campo com rotulo, icone e dica — o mesmo desenho em todos os formularios. */
function Field({
  label,
  icon,
  hint,
  invalid = false,
  children,
}: {
  label: string;
  icon: React.ReactNode;
  hint?: string;
  invalid?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-bold text-mb-ink">{label}</span>
      <span
        className={cn(
          "mt-2 flex h-12 items-center gap-2.5 rounded-mb-field border bg-white px-4 focus-within:ring-[3px] focus-within:ring-mb-yellow/35",
          invalid ? "border-destructive" : "border-mb-line focus-within:border-mb-navy",
        )}
      >
        <span className="shrink-0">{icon}</span>
        {children}
      </span>
      {hint && (
        <span
          className={cn("mt-1.5 block text-xs", invalid ? "text-destructive" : "text-mb-muted")}
        >
          {hint}
        </span>
      )}
    </label>
  );
}
